import React, { useEffect, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Card,
} from '../AumhumSite/sections'
import { ArrowRight, MessageCircle, PhoneCall } from '../AumhumSite/icons'
import StepDots from './StepDots'
import {
  sendProspectOtp,
  verifyProspectOtp,
  signupPatientProspect,
  isLoggedIn,
} from 'src/consumer/services/patientProspectService'
import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'

// Step 3: account creation. We collect just enough pre-OTP for the care
// team to start matching: subject (self vs someone else), name(s), mobile
// (E.164 via react-phone-input-2 — full ITU country coverage with a
// searchable dropdown), email (optional), age, gender. The OTP confirms
// identity; the rest is sent through to /initialize so the support
// dashboard sees real names and demographics on first load instead of a
// blank prospect record.
//
// Mobile is the only mandatory field (plus consent + OTP). Everything
// else is genuinely optional — the user can always fill more on
// /care/status's progressive intake form.
//
// Mobile widget: react-phone-input-2 (same as /signup) gives us flag +
// dial-code dropdown + inline national formatting + search across all
// 240+ ITU country codes. India is preferred-first (and pre-selected)
// because AUMHUM serves primarily India + a few diaspora corridors,
// but every country is reachable via the search box.

const SUBJECT_OPTIONS = [
  { id: 'SELF', label: 'For myself' },
  { id: 'DEPENDENT', label: 'For someone else' },
]

const GENDER_OPTIONS = [
  { id: 'FEMALE', label: 'Female' },
  { id: 'MALE', label: 'Male' },
  { id: 'OTHER', label: 'Other' },
  { id: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
]

export default function MobileOtpStep({ resolvedPathway, onSignedUp, onBack, onChangePathway, intent = null }) {
  // Booking-funnel framing — the explainer copy below switches when
  // ?intent=consultation is set so visitors who came in via
  // /book-consultation see "Verify and book" rather than the broader
  // "verify and create your account" copy.
  const isConsultation = intent === 'consultation'
  const [subjectType, setSubjectType] = useState('SELF')
  const [name, setName] = useState('')
  const [subjectName, setSubjectName] = useState('')
  // E.164 digits-only WITHOUT leading + (react-phone-input-2 convention).
  // We prepend the '+' when sending to the backend.
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('')

  const [otp, setOtp] = useState('')
  const [channel, setChannel] = useState('whatsapp')
  const [sessionId, setSessionId] = useState(null)
  const [otpSent, setOtpSent] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const tickRef = useRef(null)

  useEffect(() => {
    if (resendIn <= 0) {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
      return undefined
    }
    tickRef.current = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [resendIn])

  // react-phone-input-2 emits digits-only without the leading +. The
  // wellness OTP backend expects "+digits" so we add the prefix when
  // sending. isValidPhoneNumber (libphonenumber-js) gives us strict
  // per-country E.164 validation rather than the previous permissive
  // 6–15 digit regex.
  const fullMobile = mobile ? `+${mobile}` : ''
  const validMobile = !!mobile && isValidPhoneNumber(fullMobile)

  // Trivial e-mail sanity check — the backend doesn't require email and
  // typos here are harmless, but flagging an obviously broken value
  // saves the user a round-trip.
  const emailLooksOk = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  // Age sanity: integer 0..120. Empty = optional, treat as null.
  const ageIsValid = !age || (/^\d{1,3}$/.test(age) && Number(age) >= 0 && Number(age) <= 120)

  const requestOtp = async (preferredChannel) => {
    setError('')
    if (!validMobile) {
      setError('Please enter a valid mobile number for the country you picked.')
      return
    }
    if (!emailLooksOk) {
      setError('That email doesn’t look right. Leave it blank if you’d rather skip.')
      return
    }
    if (!ageIsValid) {
      setError('Please enter a valid age (0–120) or leave it blank.')
      return
    }
    try {
      setSubmitting(true)
      // Run the v3 captcha challenge BEFORE the network call so bots
      // are dropped before we burn Twilio quota. Returns null on hosts
      // without a configured site-key (dev / preview); backend treats
      // a null token as trivially successful in that case.
      const recaptchaToken = await executeRecaptchaV3('signup_otp_send')
      const res = await sendProspectOtp(fullMobile, preferredChannel, { recaptchaToken })
      setSessionId(res.sessionId)
      setChannel(res.channel)
      setOtpSent(true)
      setResendIn(res.resendDelaySeconds || 20)
    } catch (e) {
      setError(e.message || 'Could not send the code. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const verifyAndSignup = async () => {
    setError('')
    if (!consent) {
      setError('Please agree to the privacy + terms before continuing.')
      return
    }
    setSubmitting(true)
    let verify
    try {
      verify = await verifyProspectOtp({ mobile: fullMobile, otp, sessionId, channel })
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
      return
    }

    try {
      const session = await signupPatientProspect({
        name: name.trim() || null,
        mobile: fullMobile,
        email: email.trim() || null,
        otpToken: verify.otpToken,
        pathway: resolvedPathway?.pathway,
        specialtyTag: resolvedPathway?.specialtyTag,
        subjectType,
        subjectName: subjectType !== 'SELF' && subjectName.trim() ? subjectName.trim() : null,
        age: age ? Number(age) : null,
        gender: gender || null,
        consents: { tos: true, privacy: true },
        source: 'WEB',
        bookingIntent: intent,
      })
      onSignedUp?.(session)
    } catch (e) {
      // Initialize failed but the OTP step did sign the user in. Hand off
      // to /care/status with a hard nav so the auth-aware shell reloads
      // (header, login button), and surface the failure reason on the
      // landing page rather than stranding the user on this form.
      if (isLoggedIn()) {
        const reason = e?.message || 'init_failed'
        window.location.assign(
          `/care/status?initFailed=1&reason=${encodeURIComponent(reason)}`,
        )
        return
      }
      setError(e.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  const isForSomeoneElse = subjectType !== 'SELF'

  return (
    <Container className="pps-shell">
      <StepDots step={3} of={3} />
      <Eyebrow>Last step</Eyebrow>
      <SectionTitle as="h1">Create your free account</SectionTitle>

      {/* Sets expectations BEFORE the user starts typing: this step
          verifies mobile + creates the account; the deeper intake
          (history, reports, etc.) lives on the next page. Booking
          variant tightens the language toward "let's book". */}
      <p className="pps-otp-explainer">
        {isConsultation
          ? 'Verifying your mobile creates your free AUMHUM account. After that you’ll share a few details so our care team can match you to the right specialist and book your consultation.'
          : 'Verifying your mobile creates your free AUMHUM account. On the next page you can share more details — your story, any reports, what’s been going on — so our care team can match you with the right specialist.'}
      </p>

      {resolvedPathway ? (
        <div className="pps-recap pps-recap--card">
          <div className="pps-recap-meta">
            <span className="pps-recap-kicker">You&rsquo;re booking for</span>
            <span className="pps-recap-pill">
              {resolvedPathway.topLabel}
              {resolvedPathway.subLabel ? <> · {resolvedPathway.subLabel}</> : null}
            </span>
          </div>
          {onChangePathway ? (
            <button
              type="button"
              className="pps-recap-change"
              onClick={() => {
                if (otpSent) {
                  // Confirm before nuking an in-flight OTP session — the
                  // user would have to re-request a new code anyway.
                  const ok = window.confirm(
                    'Change the type of care? You’ll need to send a new WhatsApp code after.',
                  )
                  if (!ok) return
                }
                onChangePathway()
              }}
              disabled={submitting}
            >
              Change
            </button>
          ) : null}
        </div>
      ) : null}

      <Card className="pps-card-form" emphasised>
        {/* Who is this care for — drives whether subsequent age/gender
            fields refer to the user or to a dependent. */}
        <div className="pps-field">
          <span className="pps-field-label">Who is this care request for?</span>
          <div className="pps-chip-row" role="radiogroup" aria-label="Care request is for">
            {SUBJECT_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={subjectType === opt.id}
                className={`pps-chip${subjectType === opt.id ? ' is-active' : ''}`}
                onClick={() => setSubjectType(opt.id)}
                disabled={otpSent && !error}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-name">
            Your name <span className="pps-field-optional">(optional)</span>
          </label>
          <input
            id="pps-name"
            className="pps-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name is fine"
            autoComplete="given-name"
            disabled={otpSent && !error}
          />
        </div>

        {isForSomeoneElse ? (
          <div className="pps-field">
            <label className="pps-field-label" htmlFor="pps-subject-name">
              Their name <span className="pps-field-optional">(the person who needs care)</span>
            </label>
            <input
              id="pps-subject-name"
              className="pps-input"
              type="text"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              placeholder="First name is fine"
              autoComplete="off"
              disabled={otpSent && !error}
            />
          </div>
        ) : null}

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-mobile">Mobile number</label>
          {/*
            react-phone-input-2 with search enabled — full ITU country
            coverage, not just the 12-entry shortlist we used to show.
            Reuses the same `.sv-phoneinput-wrap` styling /signup uses
            so the two surfaces look identical. `preferredCountries`
            keeps the India / diaspora corridors at the top of the
            dropdown for fast picking, but every country is reachable
            via the search box.
          */}
          <div className="sv-phoneinput-wrap">
            <PhoneInput
              inputProps={{ id: 'pps-mobile', name: 'mobile', autoComplete: 'tel' }}
              country={'in'}
              preferredCountries={['in', 'us', 'gb', 'ae', 'sg', 'au', 'ca']}
              value={mobile}
              onChange={(v) => setMobile(v)}
              enableSearch
              disabled={otpSent && !error}
              inputStyle={{ width: '100%' }}
              containerStyle={{ width: '100%' }}
              dropdownStyle={{ zIndex: 99999 }}
            />
          </div>
          <span className="pps-help">
            We&rsquo;ll send a one-time code on WhatsApp. Standard rates may apply.
          </span>
        </div>

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-email">
            Email <span className="pps-field-optional">(optional)</span>
          </label>
          <input
            id="pps-email"
            className="pps-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={otpSent && !error}
          />
          <span className="pps-help">
            For appointment confirmations and care summaries. We don&rsquo;t spam.
          </span>
        </div>

        <div className="pps-field-row">
          <div className="pps-field pps-field--half">
            <label className="pps-field-label" htmlFor="pps-age">
              {isForSomeoneElse ? 'Their age' : 'Your age'}{' '}
              <span className="pps-field-optional">(optional)</span>
            </label>
            <input
              id="pps-age"
              className="pps-input"
              type="number"
              min={0}
              max={120}
              value={age}
              onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 32"
              autoComplete="off"
              inputMode="numeric"
              disabled={otpSent && !error}
            />
          </div>

          <div className="pps-field pps-field--half">
            <span className="pps-field-label">
              {isForSomeoneElse ? 'Their gender' : 'Your gender'}{' '}
              <span className="pps-field-optional">(optional)</span>
            </span>
            <div className="pps-chip-row" role="radiogroup" aria-label="Gender">
              {GENDER_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={gender === opt.id}
                  className={`pps-chip pps-chip--small${gender === opt.id ? ' is-active' : ''}`}
                  onClick={() => setGender((cur) => (cur === opt.id ? '' : opt.id))}
                  disabled={otpSent && !error}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!otpSent ? (
          <div className="pps-cta-row">
            <button type="button" className="pps-btn is-ghost" onClick={onBack}>Back</button>
            <button
              type="button"
              className={`pps-btn is-primary${validMobile && !submitting ? '' : ' is-disabled'}`}
              disabled={!validMobile || submitting}
              onClick={() => requestOtp('whatsapp')}
            >
              {submitting ? 'Sending…' : (
                <>Send WhatsApp code <MessageCircle /></>
              )}
            </button>
          </div>
        ) : (
          <>
            <div className="pps-field">
              <label className="pps-field-label" htmlFor="pps-otp">
                Enter the 4–6 digit code we sent on {channel === 'sms' ? 'SMS' : 'WhatsApp'}
              </label>
              <div className="pps-otp-row">
                <input
                  id="pps-otp"
                  className="pps-input"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="pps-channel-toggle">
                {resendIn > 0 ? (
                  <span>Resend in {resendIn}s</span>
                ) : (
                  <button type="button" onClick={() => requestOtp(channel)}>Resend code</button>
                )}
              </div>
            </div>

            <div className="pps-checkbox-row">
              <input
                id="pps-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <label htmlFor="pps-consent">
                I agree to AUMHUM&rsquo;s <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{' '}
                <a href="/terms" target="_blank" rel="noreferrer">Terms</a>, and to share these
                details so the care team can find me a specialist.
              </label>
            </div>

            <div className="pps-cta-row">
              <button type="button" className="pps-btn is-ghost" onClick={onBack}>Back</button>
              <button
                type="button"
                className={`pps-btn is-primary${otp.length >= 4 && consent && !submitting ? '' : ' is-disabled'}`}
                disabled={otp.length < 4 || !consent || submitting}
                onClick={verifyAndSignup}
              >
                {submitting ? 'Creating…' : (<>Create account <ArrowRight /></>)}
              </button>
            </div>
          </>
        )}

        {error ? <div className="pps-error" role="alert">{error}</div> : null}

        <div className="pps-secondary-row">
          <a className="pps-btn is-ghost" href="https://wa.me/918047492952" target="_blank" rel="noreferrer">
            <PhoneCall /> Just want to talk? Chat on WhatsApp
          </a>
        </div>
      </Card>
    </Container>
  )
}
